/**
 * Data layer for the Páginas screen.
 *
 * Every mutation invalidates the list rather than patching the cache: promoting a page to main
 * demotes another one server-side, so the client cannot know the full result of its own write.
 *
 * @module Pages/BUs/Pages
 */

import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useActionToast } from '@/Hooks/useActionToast';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { pagesQueryKeys } from '../Constants/queryKeys';
import { MAX_LIST_LIMIT } from '@/Shared/constants/listLimits';
import { createPage, deletePage, getPages, updatePage } from '../Services/Pages.service';
import type {
  Page,
  PageCreatePayload,
  PageListResponse,
  PageRequest,
  PageUpdatePayload,
} from '../Types/Pages.types';

/**
 * Every page, or just one tenant's when `tenantSlug` is set.
 *
 * Asks for the maximum by default: the BFF's listing defaults to 10, and every screen here groups
 * the whole result by BU — so the default quietly showed a slice as if it were everything.
 */
export const usePages = (params: PageRequest = {}): UseQueryResult<PageListResponse> =>
  useQuery({
    queryKey: pagesQueryKeys.list(params),
    queryFn: () => getPages({ limit: MAX_LIST_LIMIT, ...params }),
  });

export const useCreatePage = (): UseMutationResult<Page, Error, PageCreatePayload> => {
  const queryClient = useQueryClient();
  const toast = useActionToast();

  return useMutation({
    mutationFn: createPage,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pagesQueryKeys.lists() });
      toast.success('Página criada');
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível criar a página', error);
    },
  });
};

export const useUpdatePage = (): UseMutationResult<
  Page,
  Error,
  { id: string; payload: PageUpdatePayload }
> => {
  const queryClient = useQueryClient();
  const toast = useActionToast();

  return useMutation({
    mutationFn: ({ id, payload }) => updatePage(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pagesQueryKeys.lists() });
      toast.success('Página salva');
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível salvar a página', error);
    },
  });
};

export const useDeletePage = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  const toast = useActionToast();

  return useMutation({
    mutationFn: deletePage,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pagesQueryKeys.lists() });
      toast.success('Página excluída');
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível excluir a página', error);
    },
  });
};
